import { useState, type CSSProperties, type ReactNode } from 'react';
import type { BrandKit } from '../lib/setup';
import type { DraftSlide } from '../lib/drafts';

interface Props {
  slide: DraftSlide;
  totalSlides: number;
  kit: BrandKit | null;
  format: 'carousel' | 'single_image';
  projectName: string;
  publicUrl: string | null;
  projectLogoUrl?: string | null;
  /** When true (default), the project's logo is a lockup that already
   *  contains the brand name — slides suppress the separate wordmark
   *  to avoid double branding. */
  logoIncludesWordmark?: boolean;
  topicTitle?: string;
}

/**
 * Premium deterministic carousel renderer.
 *
 * This is intentionally strict: readable type, generous spacing, and bounded
 * copy density. It is the visual source of truth for browser export now and
 * the server-side Designer later.
 */
export function SlidePreview({ slide, totalSlides, kit, format, projectName, publicUrl, projectLogoUrl, logoIncludesWordmark, topicTitle }: Props) {
  const palette = kit?.palette ?? defaultPalette;
  const fonts = kit?.fonts ?? { heading: 'Playfair Display', body: 'Manrope' };

  const W = 1080;
  const H = format === 'carousel' ? 1350 : 1080;

  // Luminance-aware rendering. The slide template was originally designed for
  // a light/cream background. When the operator picks a dark background, the
  // "white card" and dark body text stop working. We compute palette-derived
  // colours so dark briefs render with light body text and a slightly-lighter-
  // than-background card, instead of inverting blindly.
  const dark = isDark(palette.background);
  const textOnBg = dark ? '#f4f4f5' : palette.text;
  const textOnCard = dark ? '#f4f4f5' : palette.text;
  const cardBgValue = dark
    ? mixHex(palette.background, '#ffffff', 0.08)
    : 'rgba(255,255,255,0.88)';
  const brandMarkColor = dark ? '#f4f4f5' : palette.primary;
  const dividerColor = dark ? '#f4f4f580' : palette.secondary; // 50% white on dark

  const bgA = dark
    ? palette.background
    : mixHex(palette.background, '#ffffff', 0.08);
  const bgB = mixHex(palette.background, palette.accent, dark ? 0.10 : 0.18);
  const bgC = mixHex(palette.background, palette.primary, dark ? 0.12 : 0.2);
  const vars: CSSProperties = {
    ['--brand-primary' as never]: palette.primary,
    ['--brand-secondary' as never]: palette.secondary,
    ['--brand-accent' as never]: palette.accent,
    ['--brand-background' as never]: palette.background,
    ['--brand-text' as never]: palette.text,
    ['--text-on-bg' as never]: textOnBg,
    ['--text-on-card' as never]: textOnCard,
    ['--card-bg' as never]: cardBgValue,
    ['--brand-mark-color' as never]: brandMarkColor,
    ['--divider-color' as never]: dividerColor,
    ['--brand-font-heading' as never]: `'${fonts.heading}', ui-serif, Georgia, serif`,
    ['--brand-font-body' as never]: `'${fonts.body}', ui-sans-serif, system-ui, sans-serif`,
  };

  return (
    <div
      className="relative w-full overflow-hidden rounded-[1.25rem] border border-border-color bg-surface-2 shadow-2xl shadow-black/30"
      style={{ aspectRatio: `${W} / ${H}` }}
    >
      <div
        className="absolute top-0 left-0 origin-top-left"
        data-slide-canvas={slide.id}
        data-slide-index={slide.slideIndex}
        style={{ width: W, height: H, transform: 'scale(var(--scale))' }}
        ref={(el) => {
          if (!el) return;
          const parent = el.parentElement;
          if (!parent) return;
          const update = () => el.style.setProperty('--scale', String(parent.clientWidth / W));
          update();
          const ro = new ResizeObserver(update);
          ro.observe(parent);
        }}
      >
        <div
          className="w-full h-full relative overflow-hidden"
          style={{
            ...vars,
            background: `linear-gradient(135deg, ${bgA} 0%, ${bgB} 46%, ${bgC} 100%)`,
            color: 'var(--text-on-bg)',
            fontFamily: 'var(--brand-font-body)',
          }}
        >
          <Texture />
          {format === 'single_image' ? (
            <SingleImageSlide slide={slide} totalSlides={totalSlides} kit={kit} projectName={projectName} publicUrl={publicUrl} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark} topicTitle={topicTitle} />
          ) : slide.kind === 'cover' || slide.kind === 'cta' ? (
            <HeroSlide slide={slide} totalSlides={totalSlides} kit={kit} projectName={projectName} publicUrl={publicUrl} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark} />
          ) : slide.kind === 'bullet-list' ? (
            <FrameworkSlide slide={slide} totalSlides={totalSlides} kit={kit} projectName={projectName} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark} topicTitle={topicTitle} />
          ) : slide.kind === 'quote' ? (
            <QuoteSlide slide={slide} totalSlides={totalSlides} kit={kit} projectName={projectName} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark} />
          ) : slide.kind === 'stat' ? (
            <StatSlide slide={slide} totalSlides={totalSlides} kit={kit} projectName={projectName} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark} />
          ) : (
            <InsightSlide slide={slide} totalSlides={totalSlides} kit={kit} projectName={projectName} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark} topicTitle={topicTitle} />
          )}
        </div>
      </div>
    </div>
  );
}

function Shell({ children, kit, projectName, totalSlides, slideIndex, projectLogoUrl, logoIncludesWordmark }: {
  children: ReactNode;
  kit: BrandKit | null;
  projectName: string;
  totalSlides: number;
  slideIndex: number;
  projectLogoUrl?: string | null;
  logoIncludesWordmark?: boolean;
}) {
  return (
    <div className="absolute inset-0 px-[82px] py-[74px] flex flex-col">
      <header className="flex items-center justify-between gap-[40px] shrink-0">
        <BrandMark kit={kit} projectName={projectName} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark} />
        {totalSlides > 1 && (
          <p className="text-[34px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--brand-secondary)' }}>
            {String(slideIndex + 1).padStart(2, '0')} / {String(totalSlides).padStart(2, '0')}
          </p>
        )}
      </header>
      <main className="flex-1 min-h-0 flex flex-col justify-center">{children}</main>
    </div>
  );
}

function HeroSlide({ slide, totalSlides, kit, projectName, publicUrl, projectLogoUrl, logoIncludesWordmark }: {
  slide: DraftSlide;
  totalSlides: number;
  kit: BrandKit | null;
  projectName: string;
  publicUrl: string | null;
  projectLogoUrl?: string | null;
  logoIncludesWordmark?: boolean;
}) {
  const isCta = slide.kind === 'cta';
  return (
    <Shell kit={kit} projectName={projectName} totalSlides={totalSlides} slideIndex={slide.slideIndex} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark}>
      <div className="grid grid-rows-[auto_auto] gap-[40px] max-w-[900px]">
        {isCta && (
          <p className="text-[34px] uppercase tracking-[0.32em] font-bold" style={{ color: 'var(--brand-accent)' }}>
            Next step
          </p>
        )}
        {slide.title && <DisplayTitle text={slide.title} size={titleSize(slide.title, 124, 92)} />}
        <div className="grid grid-cols-[9px_1fr] gap-[34px] items-start max-w-[840px]">
          <div className="h-full min-h-[110px] rounded-full" style={{ backgroundColor: 'var(--brand-accent)' }} />
          <p style={{ color: 'var(--text-on-bg)', fontSize: bodySize(slide.body, 64, 52), lineHeight: 1.22 }}>{softLimit(slide.body, 260)}</p>
        </div>
        {isCta && publicUrl && (
          <div
            className="mt-[14px] inline-flex w-fit items-center gap-[26px] rounded-full px-[44px] py-[26px] shadow-xl"
            style={{ backgroundColor: 'var(--card-bg)' }}
          >
            <span className="text-[36px] uppercase tracking-[0.18em] font-semibold" style={{ color: 'var(--brand-secondary)' }}>
              Visit
            </span>
            <span
              className="text-[58px] font-black"
              style={{ color: 'var(--brand-primary)', fontFamily: 'var(--brand-font-heading)' }}
            >
              {stripScheme(publicUrl)}
            </span>
          </div>
        )}
      </div>
    </Shell>
  );
}

function FrameworkSlide({ slide, totalSlides, kit, projectName, projectLogoUrl, logoIncludesWordmark, topicTitle }: {
  slide: DraftSlide;
  totalSlides: number;
  kit: BrandKit | null;
  projectName: string;
  projectLogoUrl?: string | null;
  logoIncludesWordmark?: boolean;
  topicTitle?: string;
}) {
  const items = bulletItems(slide.body).slice(0, 4);

  // Writer sometimes produces a bullet-list slide with only one item, which
  // renders as a lonely card floating with all the whitespace below. Fall
  // back to the insight layout so the slide reads as intentional.
  if (items.length < 2) {
    return (
      <InsightSlide
        slide={slide}
        totalSlides={totalSlides}
        kit={kit}
        projectName={projectName}
        projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark}
        topicTitle={topicTitle}
      />
    );
  }

  return (
    <Shell kit={kit} projectName={projectName} totalSlides={totalSlides} slideIndex={slide.slideIndex} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark}>
      <div className="space-y-[44px]">
        {slide.title && <DisplayTitle text={slide.title} size={titleSize(slide.title, 96, 72)} />}
        <div className="grid gap-[22px]">
          {items.map((item, i) => (
            <div key={i} className="grid grid-cols-[88px_1fr] gap-[28px] items-start rounded-[34px] px-[36px] py-[30px] shadow-xl" style={{ backgroundColor: 'var(--card-bg)' }}>
              <p className="text-[48px] leading-none font-bold" style={{ color: 'var(--brand-accent)', fontFamily: 'var(--brand-font-heading)' }}>{String(i + 1).padStart(2, '0')}</p>
              <p style={{ color: 'var(--text-on-card)', fontSize: bodySize(item, 58, 46), lineHeight: 1.22 }}>{softLimit(item, 130)}</p>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}

function InsightSlide({ slide, totalSlides, kit, projectName, projectLogoUrl, logoIncludesWordmark, topicTitle }: {
  slide: DraftSlide;
  totalSlides: number;
  kit: BrandKit | null;
  projectName: string;
  projectLogoUrl?: string | null;
  logoIncludesWordmark?: boolean;
  topicTitle?: string;
}) {
  // Body slides should always carry a title. If the Writer omitted one, fall
  // back to the topic title from the draft so the slide doesn't render as an
  // orphan card floating in the middle of the canvas.
  const headline = slide.title ?? topicTitle ?? null;
  return (
    <Shell kit={kit} projectName={projectName} totalSlides={totalSlides} slideIndex={slide.slideIndex} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark}>
      <div className="grid gap-[42px]">
        {headline ? (
          <DisplayTitle text={headline} size={titleSize(headline, 104, 78)} />
        ) : (
          <p className="text-[36px] uppercase tracking-[0.24em] font-bold" style={{ color: 'var(--brand-accent)' }}>
            Insight
          </p>
        )}
        <div className="rounded-[42px] p-[64px] shadow-2xl" style={{ backgroundColor: 'var(--card-bg)' }}>
          <p className="max-w-[860px]" style={{ color: 'var(--text-on-card)', fontSize: bodySize(slide.body, 70, 56), lineHeight: 1.22 }}>{softLimit(slide.body, 360)}</p>
        </div>
      </div>
    </Shell>
  );
}

function QuoteSlide({ slide, totalSlides, kit, projectName, projectLogoUrl, logoIncludesWordmark }: {
  slide: DraftSlide;
  totalSlides: number;
  kit: BrandKit | null;
  projectName: string;
  projectLogoUrl?: string | null;
  logoIncludesWordmark?: boolean;
}) {
  return (
    <Shell kit={kit} projectName={projectName} totalSlides={totalSlides} slideIndex={slide.slideIndex} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark}>
      <div className="max-w-[850px]">
        <p className="text-[210px] leading-[0.6]" style={{ color: 'var(--brand-accent)', fontFamily: 'var(--brand-font-heading)' }}>“</p>
        <p className="font-semibold" style={{ fontFamily: 'var(--brand-font-heading)', color: 'var(--text-on-bg)', fontSize: bodySize(slide.body, 78, 60), lineHeight: 1.08 }}>{softLimit(slide.body, 220)}</p>
        {Boolean(slide.attribution) && <p className="mt-[48px] text-[36px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--brand-secondary)' }}>{slide.attribution}</p>}
        {!slide.attribution && slide.title && <p className="mt-[48px] text-[36px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--brand-secondary)' }}>— {stripBoldMarkers(slide.title)}</p>}
      </div>
    </Shell>
  );
}

function StatSlide({ slide, totalSlides, kit, projectName, projectLogoUrl, logoIncludesWordmark }: {
  slide: DraftSlide;
  totalSlides: number;
  kit: BrandKit | null;
  projectName: string;
  projectLogoUrl?: string | null;
  logoIncludesWordmark?: boolean;
}) {
  // v15: read the structured `stat` field the Designer wrote. The renderer
  // no longer guesses the focal phrase from `body` — that was the bug that
  // ate the leading "M" of "Managers" on Stichh slide 4. Fall back to the
  // first non-empty token only if Designer somehow shipped a stat slide
  // without filling the field; the upstream validator should prevent this.
  const stat = (slide.stat ?? '').trim() || slide.body.split(/\s+/)[0] || '';
  return (
    <Shell kit={kit} projectName={projectName} totalSlides={totalSlides} slideIndex={slide.slideIndex} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark}>
      <div className="rounded-[48px] p-[52px] shadow-2xl" style={{ backgroundColor: 'var(--card-bg)' }}>
        <p className="text-[184px] leading-none font-black" style={{ color: 'var(--text-on-card)', fontFamily: 'var(--brand-font-heading)' }}>{stat}</p>
        {Boolean(slide.title) && <p className="mt-[28px] font-bold" style={{ color: 'var(--text-on-card)', fontFamily: 'var(--brand-font-heading)', fontSize: titleSize(slide.title!, 60, 48), lineHeight: 1.08 }}>{stripBoldMarkers(slide.title!)}</p>}
        <p className="mt-[24px]" style={{ fontSize: bodySize(slide.body, 56, 46), lineHeight: 1.22 }}>{softLimit(slide.body, 240)}</p>
      </div>
    </Shell>
  );
}

function SingleImageSlide({ slide, totalSlides, kit, projectName, publicUrl, projectLogoUrl, logoIncludesWordmark, topicTitle }: {
  slide: DraftSlide;
  totalSlides: number;
  kit: BrandKit | null;
  projectName: string;
  publicUrl: string | null;
  projectLogoUrl?: string | null;
  logoIncludesWordmark?: boolean;
  topicTitle?: string;
}) {
  const title = slide.title ?? topicTitle ?? '';
  return (
    <>
      <div className="absolute left-0 top-0 h-[18px] w-full" style={{ backgroundColor: 'var(--brand-accent)' }} />
      <div className="absolute right-[-90px] top-[80px] h-[760px] w-[360px] rotate-[-10deg] rounded-[90px] opacity-[0.12]" style={{ backgroundColor: 'var(--brand-primary)' }} />
      <div className="absolute right-[70px] bottom-[70px] h-[250px] w-[250px] rounded-full opacity-[0.18]" style={{ backgroundColor: 'var(--brand-accent)' }} />
      <Shell kit={kit} projectName={projectName} totalSlides={totalSlides} slideIndex={slide.slideIndex} projectLogoUrl={projectLogoUrl} logoIncludesWordmark={logoIncludesWordmark}>
        <div className="grid gap-[34px] max-w-[850px]">
          <p className="text-[34px] uppercase tracking-[0.24em] font-semibold" style={{ color: 'var(--brand-accent)' }}>
            Insight
          </p>
          <DisplayTitle text={title} size={titleSize(title, 104, 78)} />
          <div className="grid grid-cols-[9px_1fr] gap-[30px] items-start max-w-[820px] rounded-[34px] p-[34px]" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="h-full min-h-[120px] rounded-full" style={{ backgroundColor: 'var(--brand-accent)' }} />
            <p style={{ color: 'var(--text-on-card)', fontSize: bodySize(slide.body, 58, 48), lineHeight: 1.22 }}>{softLimit(slide.body, 260)}</p>
          </div>
          {publicUrl && (
            <p className="mt-[4px] text-[34px] uppercase tracking-[0.12em]" style={{ color: 'var(--brand-secondary)' }}>
              {stripScheme(publicUrl)}
            </p>
          )}
        </div>
      </Shell>
    </>
  );
}

function BrandMark({
  kit,
  projectName,
  projectLogoUrl,
  logoIncludesWordmark,
}: {
  kit: BrandKit | null;
  projectName: string;
  projectLogoUrl?: string | null;
  /** When true (default), the logo image is assumed to contain the
   *  brand name — so the separate wordmark is suppressed to avoid
   *  double branding. When false (icon-only logo), wordmark renders
   *  alongside. Wordmark also renders as a fallback when no logo
   *  is configured or the image fails to load. */
  logoIncludesWordmark?: boolean;
}) {
  const logoUrl = proxiedImageUrl(kit?.logoUrl ?? projectLogoUrl ?? null);
  const [logoFailed, setLogoFailed] = useState(false);

  const hasUsableLogo = !!logoUrl && !logoFailed;
  // Default to true if undefined (matches DB default + the common case).
  const wordmarkBakedIn = logoIncludesWordmark ?? true;
  const showWordmark = !hasUsableLogo || !wordmarkBakedIn;

  return (
    <div className="flex items-center gap-[24px] min-w-0">
      {hasUsableLogo ? (
        <img src={logoUrl!} alt="" className="h-[110px] max-w-[240px] object-contain" onError={() => setLogoFailed(true)} />
      ) : (
        <span className="h-[18px] w-[18px] rounded-full" style={{ backgroundColor: 'var(--brand-accent)' }} />
      )}
      {showWordmark && (
        <p
          className="text-[40px] uppercase tracking-[0.18em] font-black truncate max-w-[640px]"
          style={{ color: 'var(--brand-mark-color)', fontFamily: 'var(--brand-font-heading)' }}
        >
          {projectName}
        </p>
      )}
    </div>
  );
}

function proxiedImageUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (raw.startsWith('/') || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  return `/api/assets/image-proxy?url=${encodeURIComponent(raw)}`;
}

function DisplayTitle({ text, size }: { text: string; size: number }) {
  // Guard against Designer/Writer wrapping the ENTIRE title in ** ** — the
  // accent emphasis is meant for 1-3 specific words, never the full headline.
  // Strip the wrapping in that case and render as plain primary.
  const trimmed = text.trim();
  const isWholeWrapped =
    trimmed.startsWith('**') &&
    trimmed.endsWith('**') &&
    trimmed.slice(2, -2).indexOf('**') === -1;
  const safeText = isWholeWrapped ? trimmed.slice(2, -2) : text;
  const parts = safeText.split(/(\*\*[^*]+\*\*)/g);
  return (
    <h2 className="font-bold leading-[0.98] tracking-[-0.045em]" style={{ fontFamily: 'var(--brand-font-heading)', color: 'var(--brand-primary)', fontSize: size }}>
      {parts.map((part, i) => part.startsWith('**') && part.endsWith('**') ? <span key={i} style={{ color: 'var(--brand-accent)' }}>{part.slice(2, -2)}</span> : <span key={i}>{part}</span>)}
    </h2>
  );
}

function titleSize(text: string, max: number, min: number): number {
  const length = plainText(text).length;
  if (length <= 28) return max;
  if (length <= 48) return Math.max(min, max - 10);
  if (length <= 68) return Math.max(min, max - 20);
  return min;
}

function bodySize(text: string, max: number, min: number): number {
  // v15.2: enforce a HARD readable floor of 54px regardless of caller.
  // Designer now hard-enforces char budgets, so body text shouldn't
  // overshoot the long range anymore. When it does, soft-limit handles
  // overflow gracefully — we don't need to shrink past readability.
  const effectiveMin = Math.max(min, 54);
  const length = plainText(text).length;
  if (length <= 90) return max;
  if (length <= 150) return Math.max(effectiveMin, max - 6);
  if (length <= 230) return Math.max(effectiveMin, max - 10);
  return effectiveMin;
}

function plainText(text: string): string {
  return text.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
}

function Texture() {
  return (
    <>
      <div className="absolute left-[-210px] top-[-180px] h-[520px] w-[520px] rounded-full opacity-[0.12]" style={{ backgroundColor: 'var(--brand-accent)' }} />
      <div className="absolute right-[-240px] top-[-160px] h-[560px] w-[560px] rounded-full opacity-[0.10]" style={{ backgroundColor: 'var(--brand-primary)' }} />
      <div className="absolute right-[-190px] bottom-[-210px] h-[520px] w-[520px] rounded-[120px] rotate-[-14deg] opacity-[0.08]" style={{ backgroundColor: 'var(--brand-primary)' }} />
      <div className="absolute inset-x-[82px] top-[145px] h-px opacity-[0.18]" style={{ backgroundColor: 'var(--brand-secondary)' }} />
      <div className="absolute inset-x-[82px] bottom-[126px] h-px opacity-[0.10]" style={{ backgroundColor: 'var(--brand-secondary)' }} />
    </>
  );
}

/**
 * Strip the **bold** accent markers from body text before rendering. Markers
 * are intended for cover/cta titles only (DisplayTitle handles them). If the
 * Designer slips them into a body, render the inner text plain rather than
 * leaving literal asterisks on the slide.
 */
function stripBoldMarkers(text: string): string {
  return text.replace(/\*\*([^*]+)\*\*/g, '$1');
}

/**
 * Relative-luminance test for the operator's chosen background. Returns true
 * if the colour is dark enough that white text reads better than the brand
 * text colour. Drives card-bg, body-text-on-gradient, and the brand mark
 * wordmark colour selection at the top of SlidePreview.
 */
function isDark(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  // YIQ luminance — simpler than full relative-luminance and matches the
  // intent here (binary "is this dark or light").
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return yiq < 128;
}

function bulletItems(body: string): string[] {
  const items = body.split(/\r?\n|·|•|; /).map((s) => s.replace(/^[-*\d.)\s]+/, '').trim()).filter(Boolean);
  return items.length ? items : [body];
}

function softLimit(text: string, max: number): string {
  const clean = stripBoldMarkers(text);
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
}

function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function mixHex(base: string, overlay: string, amount: number): string {
  const a = hexToRgb(base);
  const b = hexToRgb(overlay);
  if (!a || !b) return base;
  const mix = (x: number, y: number) => Math.round(x * (1 - amount) + y * amount);
  return `rgb(${mix(a.r, b.r)}, ${mix(a.g, b.g)}, ${mix(a.b, b.b)})`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.replace('#', '').slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

const defaultPalette = {
  primary: '#2A2438',
  secondary: '#7C7288',
  accent: '#FF5A7A',
  background: '#F6EFF3',
  text: '#342D3F',
};
